/*
 * Copyright (c) 2026 Ambits Technologies LLC. All rights reserved.
 *
 * This source code is the proprietary and confidential property of
 * Ambits Technologies LLC. Unauthorized copying, modification, distribution,
 * or use of this software, via any medium, is strictly prohibited.
 *
 * MyAmbits - AI-Powered Location Safety & Social Platform
 * https://myambits.com
 */

/**
 * Pure, Firebase/Sentry-decoupled instrumentation for the SOS trigger chain's
 * previously-SILENT failure points — the cases where a safety action fails
 * partway with no crash and no signal (see the Phase-0 silent-failure audit).
 *
 * Each helper takes a `SosInstrumentationSink` (the real Sentry adapter in
 * `useSOSEvent`/`SOSTrigger`; a mock in tests, per the CLAUDE.md DI pattern —
 * mirrors `sosEventWrite.ts`). Call sites invoke these instead of inlining
 * Sentry calls so the "which failure → which capture, which tags, what
 * context" decisions are unit-testable without any Firebase/React plumbing.
 *
 * PRIVACY: message strings are static step identifiers. Phone numbers and
 * GPS coordinates MUST NEVER be passed in — only counts, event ids, trigger
 * method, and error codes go into structured tags/extra.
 */

import type { SosBreadcrumb } from "./sosEventWrite";

type CaptureContext = {
  level?: "info" | "warning" | "error";
  tags?: Record<string, string>;
  extra?: Record<string, unknown>;
};

export interface SosInstrumentationSink {
  addBreadcrumb: (b: SosBreadcrumb) => void;
  captureMessage: (message: string, ctx: CaptureContext) => void;
  captureException: (err: unknown, ctx: CaptureContext) => void;
}

const SOS_TAGS = { area: "sos", feature: "sos" } as const;

/**
 * A trigger arrived while a SOS was already active or arming, so it was
 * dropped. Breadcrumb (not a captured issue) — to the tapper this looks like
 * success, so the trail matters, but a dropped duplicate is usually benign.
 */
export function recordTriggerDropped(
  sink: SosInstrumentationSink,
  data: { method: string; active: boolean; arming: boolean },
): void {
  sink.addBreadcrumb({
    category: "sos",
    level: "warning",
    message: "sos.trigger_dropped_already_arming",
    data,
  });
}

/**
 * The bus reached the arm gate with no profile AND no emergency contacts, so
 * nothing was armed — a silently-dropped SOS. Captured: the user may believe
 * help is coming when nothing happened.
 */
export function recordArmedWithoutProfile(
  sink: SosInstrumentationSink,
  method: string,
): void {
  sink.captureMessage("sos.armed_without_profile_or_contacts", {
    level: "warning",
    tags: { ...SOS_TAGS, phase: "arm_gate" },
    extra: { method },
  });
}

/**
 * `addProfile()` or `sos.arm()` threw. Previously a try/finally with no catch,
 * so this became an unhandled rejection — the highest-value silent failure in
 * the trigger chain. Captured as an exception with the trigger method.
 */
export function recordArmThrew(
  sink: SosInstrumentationSink,
  err: unknown,
  method: string,
): void {
  sink.captureException(err, {
    tags: { ...SOS_TAGS, phase: "arm" },
    extra: { method },
  });
}

/** No authed user at arm time — unexpected for a signed-in SOS user. */
export function recordArmNoUser(
  sink: SosInstrumentationSink,
  triggerMethod: string,
): void {
  sink.captureMessage("sos.arm_aborted_no_user", {
    level: "warning",
    tags: { ...SOS_TAGS, phase: "arm" },
    extra: { triggerMethod },
  });
}

/** `push().key` was null — the SOS event can't be created; whole arm aborts. */
export function recordArmNoEventId(
  sink: SosInstrumentationSink,
  extra: { triggerMethod: string; uid: string; profileId: string },
): void {
  sink.captureMessage("sos.arm_aborted_no_event_id", {
    level: "error",
    tags: { ...SOS_TAGS, phase: "arm" },
    extra,
  });
}

/** Confirm reached the SMS block but there were no contacts to text. */
export function recordSmsNoContacts(
  sink: SosInstrumentationSink,
  eventId: string,
): void {
  sink.captureMessage("sos.emergency_sms_no_contacts", {
    level: "warning",
    tags: { ...SOS_TAGS, phase: "emergency_sms" },
    extra: { eventId },
  });
}

/**
 * The emergency SMS send returned a non-ok outcome (HTTP non-2xx, network
 * error, or every contact filtered out as ineligible → errorDetail
 * "no_emergency_contacts"). `sendSosSms` never throws, so this was
 * breadcrumb-only before. Captured so a real Twilio/transport failure creates
 * a Sentry issue. `errorDetail` is a server code/message only — never a phone
 * number or coordinate.
 */
export function recordSmsDeliveryFailed(
  sink: SosInstrumentationSink,
  outcome: { sent: number; failed: number; errorDetail?: string },
  ctx: { eventId: string; recipientCount: number },
): void {
  sink.captureMessage("sos.emergency_sms_delivery_failed", {
    level: "error",
    tags: {
      ...SOS_TAGS,
      phase: "emergency_sms",
      errorDetail: outcome.errorDetail ?? "unknown",
    },
    extra: {
      eventId: ctx.eventId,
      sent: outcome.sent,
      failed: outcome.failed,
      recipientCount: ctx.recipientCount,
    },
  });
}
